import { useEffect, useRef } from "react";

interface TurnstileApi {
  render(
    container: HTMLElement,
    options: {
      sitekey: string;
      action: string;
      theme: "light";
      appearance: "interaction-only";
      callback: (token: string) => void;
      "expired-callback": () => void;
      "timeout-callback": () => void;
      "error-callback": () => void;
    },
  ): string | undefined;
  remove(widgetId: string): void;
}

let loadingApi: Promise<TurnstileApi> | undefined;

/** Loads Cloudflare's script once, the same way the website's forms do. */
function loadApi(): Promise<TurnstileApi> {
  if (loadingApi) return loadingApi;
  const script = document.createElement("script");
  script.crossOrigin = "anonymous";
  const pending = new Promise<TurnstileApi>((resolve, reject) => {
    script.src = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";
    script.async = true;
    script.onload = () => {
      const api = (window as Window & { turnstile?: TurnstileApi }).turnstile;
      if (api) resolve(api);
      else reject(new Error("Turnstile did not initialize"));
    };
    script.onerror = () => reject(new Error("Turnstile could not load"));
    document.head.appendChild(script);
  });
  loadingApi = pending.catch((error: unknown) => {
    loadingApi = undefined;
    script.remove();
    throw error;
  });
  return loadingApi;
}

/**
 * Cloudflare Turnstile, shown only when Cloudflare needs an interaction. Tokens are single
 * use, so the form remounts this (with a new key) after every request.
 */
export function SpamCheck({
  siteKey,
  action,
  onToken,
  onError,
}: {
  siteKey: string;
  action: string;
  onToken: (token: string) => void;
  onError: () => void;
}) {
  const container = useRef<HTMLDivElement>(null);
  const callbacks = useRef({ onToken, onError });
  callbacks.current = { onToken, onError };
  useEffect(() => {
    let disposed = false;
    let widget: { api: TurnstileApi; id: string } | undefined;
    loadApi()
      .then((api) => {
        if (disposed || !container.current) return;
        const id = api.render(container.current, {
          sitekey: siteKey,
          action,
          theme: "light",
          appearance: "interaction-only",
          callback: (token) => callbacks.current.onToken(token),
          "expired-callback": () => callbacks.current.onToken(""),
          "timeout-callback": () => callbacks.current.onToken(""),
          "error-callback": () => {
            callbacks.current.onToken("");
            callbacks.current.onError();
          },
        });
        if (id === undefined) callbacks.current.onError();
        else widget = { api, id };
      })
      .catch(() => {
        if (!disposed) callbacks.current.onError();
      });
    return () => {
      disposed = true;
      if (widget) widget.api.remove(widget.id);
    };
  }, [siteKey, action]);
  return <div ref={container} className="spam-check" />;
}
